// Settings Route
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/providers/AuthProvider';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useState } from 'react';
import { User, Shield, Bell, Palette, Save, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings')({
  component: Settings,
});

function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'appearance'>('profile');

  const { data: profile } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => api.users.me(),
  });

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and preferences
        </p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-64 space-y-1">
          <SettingsNavItem
            icon={<User className="w-5 h-5" />}
            label="Profile"
            active={activeTab === 'profile'}
            onClick={() => setActiveTab('profile')}
          />
          <SettingsNavItem
            icon={<Shield className="w-5 h-5" />}
            label="Security"
            active={activeTab === 'security'}
            onClick={() => setActiveTab('security')}
          />
          <SettingsNavItem
            icon={<Bell className="w-5 h-5" />}
            label="Notifications"
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
          />
          <SettingsNavItem
            icon={<Palette className="w-5 h-5" />}
            label="Appearance"
            active={activeTab === 'appearance'}
            onClick={() => setActiveTab('appearance')}
          />
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && <ProfileSettings profile={profile} user={user} />}
          {activeTab === 'security' && <SecuritySettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'appearance' && <AppearanceSettings />}
        </div>
      </div>
    </div>
  );
}

function SettingsNavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors',
        active
          ? 'bg-accent-500/10 text-accent-500'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ProfileSettings({ profile, user }: { profile: any; user: any }) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(profile?.firstName || '');
  const [lastName, setLastName] = useState(profile?.lastName || '');

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.users.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({ firstName, lastName });
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold mb-6">Profile Information</h2>

        <div className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-accent-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user?.name?.charAt(0) || '?'}
            </div>
            <div>
              <p className="font-medium">{user?.name || 'User'}</p>
              <p className="text-sm text-muted-foreground">{user?.username}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={profile?.email || user?.username || ''}
              disabled
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Email is managed by your identity provider
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Role</label>
            <input
              type="text"
              value={profile?.role || 'viewer'}
              disabled
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed capitalize"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-400 disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <h2 className="text-lg font-semibold mb-6">Security</h2>
      <div className="space-y-6">
        <div className="p-4 bg-muted rounded-lg">
          <p className="font-medium">Authentication</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your account is secured with Microsoft Entra ID. Password and MFA settings are managed through your organization.
          </p>
        </div>

        <div>
          <h3 className="font-medium mb-3">Active Sessions</h3>
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Current Session</p>
                <p className="text-sm text-muted-foreground">
                  {navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                   navigator.userAgent.includes('Firefox') ? 'Firefox' :
                   navigator.userAgent.includes('Safari') ? 'Safari' : 'Browser'} on {
                   navigator.userAgent.includes('Windows') ? 'Windows' :
                   navigator.userAgent.includes('Mac') ? 'macOS' :
                   navigator.userAgent.includes('Linux') ? 'Linux' : 'Unknown'}
                </p>
              </div>
              <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationSettings() {
  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <h2 className="text-lg font-semibold mb-6">Notifications</h2>
      <div className="space-y-4">
        <NotificationToggle
          label="Analysis Complete"
          description="Get notified when an analysis finishes processing"
          defaultChecked
        />
        <NotificationToggle
          label="Processing Errors"
          description="Get notified when a document fails to process"
          defaultChecked
        />
        <NotificationToggle
          label="Weekly Summary"
          description="Receive a weekly summary of your analysis activity"
        />
      </div>
    </div>
  );
}

function NotificationToggle({
  label,
  description,
  defaultChecked = false,
}: {
  label: string;
  description: string;
  defaultChecked?: boolean;
}) {
  const [checked, setChecked] = useState(defaultChecked);

  return (
    <div className="flex items-center justify-between p-4 border border-border rounded-lg">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <button
        onClick={() => setChecked(!checked)}
        className={cn(
          'w-12 h-6 rounded-full transition-colors',
          checked ? 'bg-accent-500' : 'bg-muted'
        )}
      >
        <span
          className={cn(
            'block w-5 h-5 bg-white rounded-full shadow transition-transform',
            checked ? 'translate-x-6' : 'translate-x-0.5'
          )}
        />
      </button>
    </div>
  );
}

function AppearanceSettings() {
  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <h2 className="text-lg font-semibold mb-6">Appearance</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-3">Theme</label>
          <div className="grid grid-cols-3 gap-4">
            <ThemeOption label="Light" active />
            <ThemeOption label="Dark" />
            <ThemeOption label="System" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Dark mode coming in Phase 2
          </p>
        </div>
      </div>
    </div>
  );
}

function ThemeOption({ label, active = false }: { label: string; active?: boolean }) {
  return (
    <button
      className={cn(
        'p-4 border rounded-lg text-center transition-colors',
        active ? 'border-accent-500 bg-accent-500/5' : 'border-border hover:border-accent-500/50'
      )}
    >
      {label}
    </button>
  );
}

